import type { DiscoverReactionRow, DiscoverResponse, ReactionUserEntry } from '@/types';

/**
 * Aggregate a recommendation's reaction join rows into per-emoji counts and the
 * current user's own reaction. Pure, so the tallying logic is unit-testable.
 */
export function aggregateReactions(
  rows: DiscoverResponse['recommendations_to_users'],
  userId: number | null,
): { counts: Record<string, number>; userReaction: string | null } {
  const counts: Record<string, number> = {};
  let userReaction: string | null = null;
  for (const r of rows ?? []) {
    const v = r.reaction?.value;
    if (!v) continue;
    counts[v] = (counts[v] ?? 0) + 1;
    if (userId != null && r.receiver?.id === userId) userReaction = v;
  }
  return { counts, userReaction };
}

/**
 * C1 — the emoji the user ends up with after tapping `emoji`, mirroring the
 * backend toggle exactly (`reactToRecommendation`): tapping the reaction you
 * already hold removes it, anything else replaces it.
 */
export function nextReaction(current: string | null, tapped: string): string | null {
  return current === tapped ? null : tapped;
}

/** Placeholder id for a reaction row the server hasn't acknowledged yet. */
export const OPTIMISTIC_REACTION_ROW_ID = -1;

/**
 * C1 — rewrite the viewer's own join row inside a recommendation's reaction
 * rows, leaving everyone else's untouched.
 *
 * This is what makes a reaction survive: `useReactions` used to keep the result
 * in local `useState`, so swiping the card away, switching tab or pulling to
 * refresh remounted the hook and re-seeded it from the (never-updated) query
 * cache — the reaction visibly disappeared. Writing back through this helper
 * makes the cache the single source of truth, and as a bonus the C2 reactions
 * sheet (which reads the same rows) shows the new reactor without a refresh.
 *
 * Un-reacting keeps the row and nulls its reaction, exactly like the backend:
 * `aggregateReactions` and `reactionUserEntries` both skip value-less rows.
 */
export function applyReactionToRows(
  rows: DiscoverReactionRow[] | undefined,
  me: { id: number; username?: string | null; avatar_url?: string | null },
  emoji: string | null,
): DiscoverReactionRow[] {
  const myUsername = me.username ?? undefined;
  const list = rows ?? [];
  const index = list.findIndex((r) => r.receiver?.id === me.id);

  if (index === -1) {
    if (!emoji) return [...list];
    return [
      ...list,
      {
        id: OPTIMISTIC_REACTION_ROW_ID,
        reaction: { value: emoji },
        receiver: {
          id: me.id,
          username: myUsername,
          avatar_url: me.avatar_url ?? null,
          // Never a "friend" of yourself — `orderReactors` ranks you first anyway.
          isFriend: false,
        },
      },
    ];
  }

  const next = [...list];
  const mine = next[index];
  next[index] = {
    ...mine,
    reaction: emoji ? { value: emoji } : null,
    receiver: {
      ...mine.receiver,
      id: me.id,
      username: mine.receiver?.username ?? myUsername,
      avatar_url: mine.receiver?.avatar_url ?? me.avatar_url ?? null,
    },
  };
  return next;
}

/** Reaction entries sorted most-used first (used for chips + the picker badge). */
export function sortedReactionEntries(counts: Record<string, number>): [string, number][] {
  return Object.entries(counts)
    .filter(([, n]) => n > 0)
    .sort((a, b) => b[1] - a[1]);
}

/**
 * C2 — who reacted: map the join rows to {emoji, user} entries for the
 * reactions sheet. Rows lacking a reaction or a fully-populated receiver
 * (legacy/anonymous payloads) are skipped.
 */
export function reactionUserEntries(
  rows: DiscoverResponse['recommendations_to_users'],
): ReactionUserEntry[] {
  const out: ReactionUserEntry[] = [];
  for (const r of rows ?? []) {
    const emoji = r.reaction?.value;
    const u = r.receiver;
    if (!emoji || !u || typeof u.username !== 'string') continue;
    out.push({
      emoji,
      user: {
        id: u.id,
        username: u.username,
        avatar_url: u.avatar_url ?? null,
        isFriend: u.isFriend === true,
      },
    });
  }
  return out;
}

/** The "All" pseudo-tab of the reactions sheet. */
export const ALL_REACTIONS_TAB = '__all__';

/**
 * C2 — tab strip for the reactions sheet: "All" first with the grand total,
 * then one tab per emoji, most-used first.
 *
 * reco-sheet-per-gesture — the counts are the GLOBAL ones (`entries`, every
 * reaction row) whenever the caller has them. In a private circle the backend
 * names only that circle's members, so the named reactors are a subset; the
 * sheet says the difference ("+ N others from other circles"). The reactor rows
 * are only a fallback when no counts were passed.
 */
export function reactionTabs(
  entries: [string, number][],
  users?: ReactionUserEntry[],
): [string, number][] {
  const counts = entries.length
    ? Object.fromEntries(entries)
    : (users ?? []).reduce<Record<string, number>>((acc, u) => {
        acc[u.emoji] = (acc[u.emoji] ?? 0) + 1;
        return acc;
      }, {});
  const perEmoji = sortedReactionEntries(counts);
  const total = perEmoji.reduce((n, [, c]) => n + c, 0);
  return [[ALL_REACTIONS_TAB, total], ...perEmoji];
}

/**
 * C2 — Instagram-style ordering: me first (people look for themselves), then
 * the circle mates the backend flagged as friends, then everybody else. Each
 * bucket is alphabetical so the list is stable between renders. Pure + sorted
 * on a copy, never in place.
 */
export function orderReactors(
  users: ReactionUserEntry[],
  meId?: number | null,
): ReactionUserEntry[] {
  const rank = (e: ReactionUserEntry) => {
    if (meId != null && e.user.id === meId) return 0;
    return e.user.isFriend ? 1 : 2;
  };
  return [...users].sort((a, b) => {
    const d = rank(a) - rank(b);
    if (d !== 0) return d;
    return (a.user.username ?? '').localeCompare(b.user.username ?? '');
  });
}
